import { useRef, useState } from "react";
import { Camera, Trash2, Loader2, ScanLine, ImageUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LuogoSelect } from "@/components/LuogoSelect";
import { StatusBadge } from "@/components/StatusBadge";

export const ITALIA = "100000100";
export const HEADS = ["16", "17", "18"];
export const emptyGuest = (tipo = "16") => ({
  tipo, cognome: "", nome: "", sesso: "", data_nascita: "", nascita_stato: "", nascita_comune: "", nascita_provincia: "",
  cittadinanza: "", documento_tipo: "", documento_numero: "", documento_luogo: "", residenza_stato: "", residenza_comune: "", photo_ids: [],
});

const SCAN_KEYS = ["cognome", "nome", "sesso", "data_nascita", "documento_numero", "documento_tipo", "cittadinanza",
  "nascita_stato", "nascita_comune", "nascita_provincia", "residenza_stato", "residenza_comune", "documento_luogo"];

export function applyScan(g, data) {
  const f = data.fields || {};
  const patch = { photo_ids: [...(g.photo_ids || []), data.photo_id] };
  if (data.mrz_found && (g.photo_ids || []).length) SCAN_KEYS.forEach((k) => { patch[k] = ""; });
  SCAN_KEYS.forEach((k) => { if (f[k]) patch[k] = f[k]; });
  if (f.cittadinanza && !g.nascita_stato) patch.nascita_stato = f.cittadinanza;
  if (f.cittadinanza && !g.residenza_stato) patch.residenza_stato = f.cittadinanza;
  return { ...g, ...patch };
}

export function shrinkImage(file, max = 2000) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement("canvas");
      c.width = Math.round(img.naturalWidth * s);
      c.height = Math.round(img.naturalHeight * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(img.src);
      c.toBlob((b) => resolve(b ? new File([b], "documento.jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.9);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

function AutoFill({ i, t, onScan }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState("");
  const tid = (s) => `guest-${i}-${s}`;
  const go = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = "";
    if (!raw) return;
    setPreview(URL.createObjectURL(raw));
    setBusy(true);
    setMsg(null);
    const f = await shrinkImage(raw);
    const res = await onScan(i, f);
    setMsg(res);
    setBusy(false);
  };
  const filled = msg?.fields ? SCAN_KEYS.filter((k) => msg.fields[k]) : [];
  return (
    <div className="rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-4 flex flex-col sm:flex-row gap-4" data-testid={tid("autofill-panel")}>
      {preview ? <img src={preview} alt="" className="h-24 w-36 rounded-lg object-cover border" /> : <div className="grid h-24 w-36 shrink-0 place-items-center rounded-lg bg-white border"><ScanLine className="h-8 w-8 text-primary" /></div>}
      <div className="flex-1 space-y-2">
        <p className="font-display font-bold">{t("autoTitle")}</p>
        <p className="text-xs text-stone-600">{t("autoHint")}</p>
        <div className="flex flex-wrap gap-2">
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={go} data-testid={tid("doc-camera-input")} />
          <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={go} data-testid={tid("doc-file-input")} />
          <Button type="button" size="sm" onClick={() => camRef.current?.click()} disabled={busy} data-testid={tid("scan-doc-button")}>
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} {busy ? t("scanning") : t("takePhoto")}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => galRef.current?.click()} disabled={busy} data-testid={tid("upload-doc-button")}><ImageUp className="h-4 w-4 mr-1" />{t("fromGallery")}</Button>
        </div>
        {msg && (
          <p className={`rounded-lg px-3 py-2 text-sm ${msg.mrz_found ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`} data-testid={tid("scan-message")}>
            {msg.mrz_found ? `${t("mrzOk")} ${t("filledFields")}: ${filled.length}` : `${t("mrzKo")} ${t("ocrTip")}`}
          </p>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-stone-600">{label}</Label>
      {children}
    </div>
  );
}

function Pick({ value, onChange, options, placeholder, disabled, testId }) {
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-11 bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-option-${v}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function GuestCard({ g, i, t, docs, update, remove, onScan, readOnly, canRemove }) {
  const set = (k) => (v) => update(i, { [k]: v });
  const lp = { searchPlaceholder: t("typeName"), emptyText: t("noResults"), placeholder: t("search"), disabled: readOnly };
  const tid = (s) => `guest-${i}-${s}`;
  const head = HEADS.includes(String(g.tipo));
  return (
    <div className="rounded-2xl border bg-white p-4 sm:p-6 space-y-5 fade-up" data-testid={`guest-card-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 font-display font-bold text-primary">{i + 1}</span>
          <div>
            <p className="font-display text-lg font-bold">{g.cognome || g.nome ? `${g.cognome} ${g.nome}` : `${t("guest")} ${i + 1}`}</p>
            {g.schedina_status && <StatusBadge status={g.schedina_status} testId={tid("schedina-status")} />}
          </div>
        </div>
        <div className="flex gap-2">
          {canRemove && !readOnly && (
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} data-testid={tid("remove-button")}><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      </div>
      {onScan && !readOnly && <AutoFill i={i} t={t} onScan={onScan} />}
      {g.schedina_error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid={tid("schedina-error")}>{g.schedina_error}</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("tipo")}>
          <Pick value={String(g.tipo)} onChange={set("tipo")} disabled={readOnly} testId={tid("tipo")}
            options={["16", "17", "18", "19", "20"].map((x) => [x, t(`t${x}`)])} />
        </Field>
        <Field label={t("cognome")}><Input className="h-11" maxLength={50} value={g.cognome} onChange={(e) => set("cognome")(e.target.value)} disabled={readOnly} data-testid={tid("cognome")} /></Field>
        <Field label={t("nome")}><Input className="h-11" maxLength={30} value={g.nome} onChange={(e) => set("nome")(e.target.value)} disabled={readOnly} data-testid={tid("nome")} /></Field>
        <Field label={t("sesso")}><Pick value={String(g.sesso || "")} onChange={set("sesso")} disabled={readOnly} testId={tid("sesso")} placeholder={t("select")} options={[["1", t("m")], ["2", t("f")]]} /></Field>
        <Field label={t("nascita")}><Input className="h-11" type="date" value={g.data_nascita} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set("data_nascita")(e.target.value)} disabled={readOnly} data-testid={tid("data-nascita")} /></Field>
        <Field label={t("cittadinanza")}><LuogoSelect {...lp} kind="stato" value={g.cittadinanza} onChange={set("cittadinanza")} testId={tid("cittadinanza")} /></Field>
        <Field label={t("statoNascita")}><LuogoSelect {...lp} kind="stato" value={g.nascita_stato} onChange={(v) => update(i, { nascita_stato: v, ...(v !== ITALIA ? { nascita_comune: "", nascita_provincia: "" } : {}) })} testId={tid("stato-nascita")} /></Field>
        {g.nascita_stato === ITALIA && (
          <Field label={t("comuneNascita")}><LuogoSelect {...lp} kind="comune" value={g.nascita_comune} onChange={(v, it) => update(i, { nascita_comune: v, nascita_provincia: it?.provincia || "" })} testId={tid("comune-nascita")} /></Field>
        )}
        {head && <Field label={t("resStato")}><LuogoSelect {...lp} kind="stato" value={g.residenza_stato} onChange={(v) => update(i, { residenza_stato: v, ...(v !== ITALIA ? { residenza_comune: "" } : {}) })} testId={tid("stato-residenza")} /></Field>}
        {head && g.residenza_stato === ITALIA && (
          <Field label={t("resComune")}><LuogoSelect {...lp} kind="comune" value={g.residenza_comune} onChange={set("residenza_comune")} testId={tid("comune-residenza")} /></Field>
        )}
      </div>
      {head ? (
        <div className="grid gap-4 sm:grid-cols-3 rounded-xl bg-stone-50 p-4">
          <Field label={t("docTipo")}><Pick value={g.documento_tipo} onChange={set("documento_tipo")} disabled={readOnly} testId={tid("doc-tipo")} placeholder={t("select")} options={docs.map((d) => [d.code, d.name])} /></Field>
          <Field label={t("docNumero")}><Input className="h-11 font-mono uppercase" maxLength={20} value={g.documento_numero} onChange={(e) => set("documento_numero")(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} disabled={readOnly} data-testid={tid("doc-numero")} /></Field>
          <Field label={t("docLuogo")}><LuogoSelect {...lp} value={g.documento_luogo} onChange={set("documento_luogo")} testId={tid("doc-luogo")} /></Field>
        </div>
      ) : (
        <p className="text-xs text-stone-500">{t("headInfo")}</p>
      )}
    </div>
  );
}

export function GuestForm({ guests, setGuests, t, docs, onScan, readOnly }) {
  const update = (i, patch) => setGuests((gs) => gs.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const remove = (i) => setGuests((gs) => gs.filter((_, j) => j !== i));
  return (
    <div className="space-y-4">
      {guests.map((g, i) => (
        <GuestCard key={g.id || `n${i}`} g={g} i={i} t={t} docs={docs} update={update} remove={remove} onScan={onScan} readOnly={readOnly} canRemove={guests.length > 1} />
      ))}
    </div>
  );
}

export function defaultGuests(n) {
  if (n <= 1) return [emptyGuest("16")];
  return [emptyGuest("17"), ...Array.from({ length: n - 1 }, () => emptyGuest("19"))];
}
